import React, { createContext, useContext } from "react";
import PropTypes from "prop-types";
import useLastMessages from "./useLastMessages";

const LastMessagesStateContext = createContext(null);

// Read-only counterpart to SavedGroupsContext: fetches the viewer's conversations-by-origin index
// once and exposes it via context, so each source card can show "Last message sent <date>" without
// its own fetch. No actions context — there is nothing to mutate.
export function LastMessagesProvider({ children }) {
  const { state } = useLastMessages();
  return <LastMessagesStateContext.Provider value={state}>{children}</LastMessagesStateContext.Provider>;
}

LastMessagesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useLastMessagesState() {
  return useContext(LastMessagesStateContext);
}

// Convenience lookup for card components. Returns the ISO last_sent_at string, or null when the
// viewer has no conversation about this origin (also null before the index finishes loading —
// callers treat "no date" as "render nothing", which is the correct pre-load state too).
export function useLastMessageSentAt(originType, originId) {
  const state = useLastMessagesState();
  return state?.lastMessageIndex?.[originType]?.[originId] ?? null;
}

// Peer counterpart for source cards messaged profile-to-profile (PR profiles): keyed by the
// co-participant's profile id rather than a promo-entity origin. Returns the ISO last_sent_at
// string, or null when the viewer has no direct conversation with this profile (also null before
// the index loads — callers render nothing, the correct pre-load state).
export function usePeerLastMessageSentAt(profileId) {
  const state = useLastMessagesState();
  return state?.peerMessageIndex?.[profileId] ?? null;
}
